/**
 * Opens CoinMarketCap page for a specific cryptocurrency
 * @param {Object} coin - The coin object containing coin information
 * @param {string} coin.symbol - The cryptocurrency symbol (e.g., 'BTC', 'ETH')
 * @param {string} coin.name - The cryptocurrency name (e.g., 'Bitcoin', 'Ethereum')
 */
export const openCoinMarketCap = (coin) => {
  console.log('openCoinMarketCap called with:', coin); // Debug log
  
  if (!coin || (!coin.symbol && !coin.name)) {
    console.warn('Invalid coin data provided to openCoinMarketCap');
    return;
  }

  // Map of common cryptocurrency symbols to their CoinMarketCap slugs
  const symbolToSlugMap = {
    'BTC': 'bitcoin',
    'ETH': 'ethereum',
    'USDT': 'tether',
    'USDC': 'usd-coin',
    'BNB': 'bnb',
    'XRP': 'xrp',
    'ADA': 'cardano',
    'SOL': 'solana',
    'DOGE': 'dogecoin',
    'DOT': 'polkadot',
    'MATIC': 'polygon',
    'SHIB': 'shiba-inu',
    'AVAX': 'avalanche',
    'LTC': 'litecoin',
    'UNI': 'uniswap',
    'LINK': 'chainlink',
    'ATOM': 'cosmos',
    'TRX': 'tron',
    'ETC': 'ethereum-classic',
    'XLM': 'stellar',
    'BCH': 'bitcoin-cash',
    'ALGO': 'algorand',
    'VET': 'vechain',
    'FIL': 'filecoin',
    'ICP': 'internet-computer',
    'HBAR': 'hedera',
    'APE': 'apecoin',
    'SAND': 'the-sandbox',
    'MANA': 'decentraland',
    'CRO': 'cronos',
    'NEAR': 'near-protocol',
    'QNT': 'quant',
    'FLOW': 'flow',
    'AAVE': 'aave',
    'GRT': 'the-graph',
    'ENJ': 'enjin-coin',
    'CHZ': 'chiliz',
    'THETA': 'theta',
    'AXS': 'axie-infinity',
    'CAKE': 'pancakeswap',
    'STX': 'stacks',
    'KCS': 'kucoin-shares',
    'EGLD': 'elrond',
    'RUNE': 'thorchain',
    'FTM': 'fantom',
    'XTZ': 'tezos',
    'KLAY': 'klaytn',
    'HNT': 'helium',
    'BSV': 'bitcoin-sv',
    'EOS': 'eos',
    'WAVES': 'waves',
    'ZEC': 'zcash',
    'XMR': 'monero',
    'DASH': 'dash',
    'NEO': 'neo',
    'IOTA': 'iota',
    'COMP': 'compound',
    'YFI': 'yearn-finance',
    'SNX': 'synthetix',
    'MKR': 'maker',
    'SUSHI': 'sushiswap',
    'CRV': 'curve-dao-token',
    'BAT': 'basic-attention-token',
    'ZRX': '0x',
    'OMG': 'omg-network',
    'REN': 'ren',
    'KNC': 'kyber-network-crystal',
    'LRC': 'loopring',
    'BAND': 'band-protocol',
    'BAL': 'balancer',
    'STORJ': 'storj',
    'ICX': 'icon',
    'ONT': 'ontology',
    'ZIL': 'zilliqa',
    'QTUM': 'qtum',
    'LSK': 'lisk',
    'SC': 'siacoin',
    'DGB': 'digibyte',
    'RVN': 'ravencoin',
    'NANO': 'nano',
    'POL': 'polygon-ecosystem-token'
  };

  let url;
  
  // First try to use the symbol mapping for direct URLs
  if (coin.symbol && symbolToSlugMap[coin.symbol.toUpperCase()]) {
    const slug = symbolToSlugMap[coin.symbol.toUpperCase()];
    url = `https://coinmarketcap.com/currencies/${slug}/`;
    console.log('Using symbol mapping:', coin.symbol, '→', url); // Debug log
  } 
  // If no mapping found, try to generate from name
  else if (coin.name) {
    const urlFriendlyName = coin.name
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '') // Remove special characters
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    url = `https://coinmarketcap.com/currencies/${urlFriendlyName}/`;
    console.log('Using name mapping:', coin.name, '→', url); // Debug log
  }
  // Fallback to search if we can't generate a direct URL
  else if (coin.symbol) {
    url = `https://coinmarketcap.com/search/?q=${encodeURIComponent(coin.symbol)}`;
    console.log('Using search fallback:', coin.symbol, '→', url); // Debug log
  }

  // Open in new tab
  if (url) {
    console.log('Opening URL:', url); // Debug log
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    console.error('Could not generate URL for coin:', coin);
  }
};

/**
 * Gets CoinMarketCap URL for a specific cryptocurrency without opening it
 * @param {Object} coin - The coin object containing coin information
 * @returns {string|null} - The CoinMarketCap URL or null if invalid coin data
 */
export const getCoinMarketCapUrl = (coin) => {
  if (!coin || (!coin.symbol && !coin.name)) {
    return null;
  }

  // Use the same symbol mapping as openCoinMarketCap
  const symbolToSlugMap = {
    'BTC': 'bitcoin',
    'ETH': 'ethereum',
    'USDT': 'tether',
    'USDC': 'usd-coin',
    'BNB': 'bnb',
    'XRP': 'xrp',
    'ADA': 'cardano',
    'SOL': 'solana',
    'DOGE': 'dogecoin',
    'DOT': 'polkadot',
    'MATIC': 'polygon',
    'SHIB': 'shiba-inu',
    'AVAX': 'avalanche',
    'LTC': 'litecoin',
    'UNI': 'uniswap',
    'LINK': 'chainlink',
    'ATOM': 'cosmos',
    'POL': 'polygon-ecosystem-token'
    // ... (same mapping as above)
  };

  // First try to use the symbol mapping for direct URLs
  if (coin.symbol && symbolToSlugMap[coin.symbol.toUpperCase()]) {
    const slug = symbolToSlugMap[coin.symbol.toUpperCase()];
    return `https://coinmarketcap.com/currencies/${slug}/`;
  } 
  // If no mapping found, try to generate from name
  else if (coin.name) {
    const urlFriendlyName = coin.name
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    return `https://coinmarketcap.com/currencies/${urlFriendlyName}/`;
  }
  // Fallback to search
  else if (coin.symbol) {
    return `https://coinmarketcap.com/search/?q=${encodeURIComponent(coin.symbol)}`;
  }

  return null;
};

/**
 * Opens CoinGecko page for a specific cryptocurrency
 * @param {Object} coin - The coin object containing coin information
 */
export const openCoinGecko = (coin) => {
  if (!coin || (!coin.symbol && !coin.name)) {
    console.warn('Invalid coin data provided to openCoinGecko');
    return;
  }

  let url;
  
  if (coin.symbol) {
    url = `https://www.coingecko.com/en/search?query=${encodeURIComponent(coin.symbol)}`;
  } else if (coin.name) {
    const urlFriendlyName = coin.name
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    
    url = `https://www.coingecko.com/en/coins/${urlFriendlyName}`;
  }

  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Opens the appropriate crypto tracking website based on preference
 * @param {Object} coin - The coin object
 * @param {string} platform - 'coinmarketcap' or 'coingecko' (default: 'coinmarketcap')
 */
export const openCryptoTracker = (coin, platform = 'coinmarketcap') => {
  switch (platform.toLowerCase()) {
    case 'coingecko':
      openCoinGecko(coin);
      break;
    case 'coinmarketcap':
    default:
      openCoinMarketCap(coin);
      break;
  }
};

export default {
  openCoinMarketCap,
  getCoinMarketCapUrl,
  openCoinGecko,
  openCryptoTracker
};